(() => {
// Entrypoint for the about-us "A Letter from Bondi" deck (BB-299).
// Loaded by sections/about-letter.liquid only, so it stays out of the
// global custom-elements bundle.
const $d98670b94aabab5d$var$SLIDE_TRANSITION_MS = 840;
const $d98670b94aabab5d$var$REVEAL_DELAY_MS = 260;
const $d98670b94aabab5d$var$WHEEL_COOLDOWN_MS = 880;
const $d98670b94aabab5d$var$WHEEL_THRESHOLD = 18;
const $d98670b94aabab5d$var$SWIPE_THRESHOLD = 46;
// How many slides either side of the active one get their video fully
// buffered ahead of time.
const $d98670b94aabab5d$var$PRELOAD_RADIUS = 1;
class $d98670b94aabab5d$export$cb8496270852a3b0 extends HTMLElement {
    connectedCallback() {
        this.track = this.querySelector("[data-track]");
        this.slides = Array.from(this.querySelectorAll("[data-slide]"));
        this.inners = this.slides.map((slide)=>slide.querySelector("[data-si]"));
        this.videos = this.slides.map((slide)=>slide.querySelector("[data-video]"));
        this.soundToggles = this.slides.map((slide)=>slide.querySelector("[data-sound-toggle]"));
        this.chrome = this.querySelector("[data-chrome]");
        this.dotsWrap = this.querySelector("[data-dots]");
        this.progress = this.querySelector("[data-progress]");
        this.chapterLabel = this.querySelector("[data-chaplabel]");
        this.canvas = this.querySelector("[data-ocean]");
        if (!this.track || this.slides.length === 0) return;
        document.documentElement.classList.add("about-letter-open");
        this.buildDots();
        // Hide slide content for the entrance animation (progressive enhancement).
        this.inners.forEach((inner)=>{
            if (!inner) return;
            inner.style.opacity = "0";
            inner.style.transform = "translateY(22px)";
            inner.style.transition = "opacity .9s cubic-bezier(.2,.6,.2,1), transform .9s cubic-bezier(.2,.6,.2,1)";
        });
        this.applyState();
        this.timeouts.push(window.setTimeout(()=>this.revealInner(0), 200));
        this.startOcean();
        this.querySelectorAll("[data-goto]").forEach((trigger)=>{
            trigger.addEventListener("click", ()=>{
                this.go(parseInt(trigger.getAttribute("data-goto") || "0", 10));
            });
        });
        this.soundToggles.forEach((toggle, toggleIndex)=>{
            const video = this.videos[toggleIndex];
            if (!toggle || !video) return;
            toggle.addEventListener("click", (event)=>{
                event.stopPropagation();
                video.muted = !video.muted;
                this.updateSoundToggle(toggleIndex);
            });
        });
        window.addEventListener("keydown", this.onKeyDown);
        window.addEventListener("wheel", this.onWheel, {
            passive: false
        });
        window.addEventListener("touchstart", this.onTouchStart, {
            passive: true
        });
        window.addEventListener("touchend", this.onTouchEnd, {
            passive: true
        });
        window.addEventListener("resize", this.onResize);
        document.addEventListener("shopify:block:select", this.onBlockSelect);
    }
    disconnectedCallback() {
        document.documentElement.classList.remove("about-letter-open");
        this.videos.forEach((video)=>video?.pause());
        if (this.rafId) cancelAnimationFrame(this.rafId);
        this.timeouts.forEach((id)=>window.clearTimeout(id));
        this.timeouts = [];
        window.removeEventListener("keydown", this.onKeyDown);
        window.removeEventListener("wheel", this.onWheel);
        window.removeEventListener("touchstart", this.onTouchStart);
        window.removeEventListener("touchend", this.onTouchEnd);
        window.removeEventListener("resize", this.onResize);
        document.removeEventListener("shopify:block:select", this.onBlockSelect);
    }
    go(target) {
        const index = Math.max(0, Math.min(this.slides.length - 1, target));
        if (index === this.index || this.animating) return;
        this.resetInner(this.index);
        this.index = index;
        this.animating = true;
        this.applyState();
        this.timeouts.push(window.setTimeout(()=>this.revealInner(index), $d98670b94aabab5d$var$REVEAL_DELAY_MS));
        this.timeouts.push(window.setTimeout(()=>{
            this.animating = false;
        }, $d98670b94aabab5d$var$SLIDE_TRANSITION_MS));
    }
    applyState() {
        const index = this.index;
        const last = this.slides.length - 1;
        if (this.track) this.track.style.transform = `translateY(${-index * 100}dvh)`;
        // Chrome is hidden on the cover only.
        const showChrome = index !== 0;
        [
            this.chrome,
            this.dotsWrap
        ].forEach((el)=>{
            if (el) el.style.opacity = showChrome ? "1" : "0";
        });
        if (this.chapterLabel) this.chapterLabel.textContent = this.slides[index].getAttribute("data-chapter") || "";
        if (this.progress && last > 0) this.progress.style.width = `${index / last * 100}%`;
        this.dots.forEach((dot, dotIndex)=>{
            dot.style.opacity = dotIndex === index ? "1" : "0.28";
        });
        this.slides.forEach((slide, slideIndex)=>{
            slide.classList.toggle("is-active", slideIndex === index);
        });
        // Video slides wash the page dark, so the chrome switches to cream.
        this.classList.toggle("is-dark", this.slides[index].hasAttribute("data-video-slide"));
        this.syncVideos();
    }
    // Play the active slide's video, rewind every other one so it starts from
    // the top the next time the deck lands on it.
    //
    // Videos are marked up as `preload="metadata"` so a deck with several of them
    // doesn't fetch every full-resolution MP4 on load. Neighbours are promoted to
    // a full preload as the deck approaches, which buys enough lead time to have
    // the opening buffered before the slide lands.
    syncVideos() {
        this.videos.forEach((video, videoIndex)=>{
            if (!video) return;
            if (video.preload !== "auto" && Math.abs(videoIndex - this.index) <= $d98670b94aabab5d$var$PRELOAD_RADIUS) {
                video.preload = "auto";
                // play() below already forces the active video to load; calling load()
                // on it would abort and restart what is about to play.
                if (videoIndex !== this.index) video.load();
            }
            if (videoIndex === this.index) {
                // Browsers reject play() when they refuse to start the media; there
                // is nothing to do about it, so don't let it surface as an error.
                const played = video.play();
                if (played) played.catch(()=>undefined);
            } else {
                video.pause();
                if (video.currentTime > 0) video.currentTime = 0;
                // Every video starts muted again the next time its slide becomes
                // active, regardless of whether the visitor unmuted it last time.
                video.muted = true;
                this.updateSoundToggle(videoIndex);
            }
        });
    }
    updateSoundToggle(index) {
        const toggle = this.soundToggles[index];
        const video = this.videos[index];
        if (!toggle || !video) return;
        toggle.setAttribute("aria-pressed", String(!video.muted));
        toggle.setAttribute("aria-label", video.muted ? "Unmute video" : "Mute video");
    }
    // One dot per slide, grouped by chapter (consecutive slides sharing a
    // data-chapter value share a group).
    buildDots() {
        const wrap = this.dotsWrap;
        if (!wrap) return;
        wrap.replaceChildren();
        this.dots = [];
        let group = null;
        let previousChapter = null;
        this.slides.forEach((slide, index)=>{
            const chapter = slide.getAttribute("data-chapter") || "";
            if (!group || chapter !== previousChapter) {
                group = document.createElement("span");
                wrap.appendChild(group);
                previousChapter = chapter;
            }
            const dot = document.createElement("i");
            dot.addEventListener("click", ()=>this.go(index));
            group.appendChild(dot);
            this.dots.push(dot);
        });
    }
    revealInner(index) {
        const inner = this.inners[index];
        if (!inner) return;
        inner.style.opacity = "1";
        inner.style.transform = "translateY(0)";
    }
    resetInner(index) {
        const inner = this.inners[index];
        if (!inner) return;
        inner.style.opacity = "0";
        inner.style.transform = "translateY(22px)";
    }
    resizeCanvas() {
        const canvas = this.canvas;
        const context = canvas ? canvas.getContext("2d") : null;
        if (!canvas || !context) return;
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const rect = canvas.getBoundingClientRect();
        canvas.width = Math.max(1, rect.width) * dpr;
        canvas.height = Math.max(1, rect.height) * dpr;
        context.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
    // Animated dawn-water background on the cover slide.
    startOcean() {
        const canvas = this.canvas;
        const context = canvas ? canvas.getContext("2d") : null;
        if (!canvas || !context) return;
        this.resizeCanvas();
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const draw = (timestamp)=>{
            const time = timestamp / 1000;
            const width = Math.max(1, canvas.width / dpr);
            const height = Math.max(1, canvas.height / dpr);
            // Vertical wash: cream sky -> warm horizon -> muted water.
            const wash = context.createLinearGradient(0, 0, 0, height);
            wash.addColorStop(0, "#ECE6D9");
            wash.addColorStop(0.42, "#E7E0D0");
            wash.addColorStop(0.58, "#DCD7C8");
            wash.addColorStop(1, "#C4CAC4");
            context.fillStyle = wash;
            context.fillRect(0, 0, width, height);
            // Soft dawn glow, high center.
            const glowY = height * 0.3;
            const glow = context.createRadialGradient(width * 0.5, glowY, 0, width * 0.5, glowY, Math.max(width, height) * 0.55);
            glow.addColorStop(0, "rgba(255,251,242,0.55)");
            glow.addColorStop(0.5, "rgba(255,248,236,0.14)");
            glow.addColorStop(1, "rgba(255,248,236,0)");
            context.fillStyle = glow;
            context.fillRect(0, 0, width, height);
            // Moving light on the water (lower half).
            const waterTop = height * 0.5;
            const bands = 22;
            const segments = 26;
            for(let band = 0; band < bands; band++){
                const progress = band / (bands - 1);
                const rowY = waterTop + progress * (height - waterTop);
                const perspective = 0.4 + progress * 1.6;
                const sway = Math.sin(time * 0.6 + band * 0.9) * (3 + progress * 10);
                const amplitude = 0.05 + progress * 0.16;
                const shimmer = (Math.sin(time * 1.1 + band * 1.7) + 1) / 2;
                context.strokeStyle = `rgba(255,252,244,${(amplitude * (0.5 + shimmer * 0.5)).toFixed(3)})`;
                context.lineWidth = perspective;
                context.beginPath();
                for(let segment = 0; segment <= segments; segment++){
                    const x = segment / segments * width;
                    const y = rowY + Math.sin(time * 0.8 + segment * 0.5 + band) * (1.2 + progress * 3) + sway * 0.1;
                    if (segment === 0) context.moveTo(x, y);
                    else context.lineTo(x, y);
                }
                context.stroke();
                // Subtle trough shadow.
                context.strokeStyle = `rgba(120,124,116,${(amplitude * 0.35).toFixed(3)})`;
                context.lineWidth = perspective * 0.8;
                context.beginPath();
                for(let segment = 0; segment <= segments; segment++){
                    const x = segment / segments * width;
                    const y = rowY + 2.4 + Math.sin(time * 0.8 + segment * 0.5 + band) * (1.2 + progress * 3) + sway * 0.1;
                    if (segment === 0) context.moveTo(x, y);
                    else context.lineTo(x, y);
                }
                context.stroke();
            }
            // Gentle vignette to seat the type.
            const vignette = context.createRadialGradient(width * 0.5, height * 0.46, height * 0.2, width * 0.5, height * 0.5, height * 0.9);
            vignette.addColorStop(0, "rgba(234,227,212,0)");
            vignette.addColorStop(1, "rgba(228,221,205,0.5)");
            context.fillStyle = vignette;
            context.fillRect(0, 0, width, height);
        };
        const loop = (timestamp)=>{
            // Only animate while the cover is showing.
            if (this.index === 0) draw(timestamp);
            this.rafId = requestAnimationFrame(loop);
        };
        this.rafId = requestAnimationFrame(loop);
    }
    constructor(...args){
        super(...args), this.track = null, this.slides = [], this.inners = [], this.dots = [], this.chrome = null, this.dotsWrap = null, this.progress = null, this.chapterLabel = null, this.canvas = null, this.videos = [], this.soundToggles = [], this.index = 0, this.animating = false, this.wheelLocked = false, this.touchStartX = 0, this.touchStartY = 0, this.rafId = 0, this.timeouts = [], this.onKeyDown = (event)=>{
            if (event.defaultPrevented) return;
            if ([
                "ArrowRight",
                "ArrowDown",
                "PageDown",
                " "
            ].includes(event.key)) {
                event.preventDefault();
                this.go(this.index + 1);
            } else if ([
                "ArrowLeft",
                "ArrowUp",
                "PageUp"
            ].includes(event.key)) {
                event.preventDefault();
                this.go(this.index - 1);
            } else if (event.key === "Home") {
                event.preventDefault();
                this.go(0);
            } else if (event.key === "End") {
                event.preventDefault();
                this.go(this.slides.length - 1);
            }
        }, // Only advance the deck when the active slide can't scroll any further,
        // so slides taller than the viewport stay reachable.
        this.onWheel = (event)=>{
            const active = this.slides[this.index];
            const canScroll = active && active.scrollHeight > active.clientHeight + 2;
            if (canScroll) {
                const atTop = active.scrollTop <= 0;
                const atBottom = active.scrollTop + active.clientHeight >= active.scrollHeight - 2;
                if (!(event.deltaY > 0 && atBottom || event.deltaY < 0 && atTop)) return;
            }
            if (this.wheelLocked || this.animating) {
                event.preventDefault();
                return;
            }
            if (Math.abs(event.deltaY) < $d98670b94aabab5d$var$WHEEL_THRESHOLD && Math.abs(event.deltaX) < $d98670b94aabab5d$var$WHEEL_THRESHOLD) return;
            event.preventDefault();
            const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
            this.go(this.index + (delta > 0 ? 1 : -1));
            this.wheelLocked = true;
            this.timeouts.push(window.setTimeout(()=>{
                this.wheelLocked = false;
            }, $d98670b94aabab5d$var$WHEEL_COOLDOWN_MS));
        }, this.onTouchStart = (event)=>{
            this.touchStartX = event.touches[0].clientX;
            this.touchStartY = event.touches[0].clientY;
        }, this.onTouchEnd = (event)=>{
            const deltaX = event.changedTouches[0].clientX - this.touchStartX;
            const deltaY = event.changedTouches[0].clientY - this.touchStartY;
            if (Math.abs(deltaX) < $d98670b94aabab5d$var$SWIPE_THRESHOLD && Math.abs(deltaY) < $d98670b94aabab5d$var$SWIPE_THRESHOLD) return;
            if (Math.abs(deltaX) >= Math.abs(deltaY)) this.go(this.index + (deltaX < 0 ? 1 : -1));
            else this.go(this.index + (deltaY < 0 ? 1 : -1));
        }, this.onResize = ()=>{
            this.resizeCanvas();
        }, // Theme editor: jump to the slide whose block was selected.
        this.onBlockSelect = (event)=>{
            const target = event.target;
            const slide = target ? target.closest("[data-slide]") : null;
            if (!slide || !this.contains(slide)) return;
            const index = this.slides.indexOf(slide);
            if (index >= 0) this.go(index);
        };
    }
}
if (!customElements.get("about-letter")) customElements.define("about-letter", $d98670b94aabab5d$export$cb8496270852a3b0);



})();
//# sourceMappingURL=about-letter.js.map
